import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput, todayStr, firstDayOfMonthStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import {
  Package,
  Calendar,
  Search,
  Download,
  ChevronUp,
  ChevronDown,
  Layers,
  Eye,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader,
  TrendingUp
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_BASE = 'https://apisaleeasy.psisat.com/api';

const BundleProductsComponent = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'sales_bills', direction: 'desc' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleDetail, setBundleDetail] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    setDateFrom(firstDayOfMonthStr());
    setDateTo(todayStr());
  }, []);

  // Fetch all quotation pages to find bundle_code in products
  const fetchBundleSalesFromQuotations = async (from, to) => {
    const bundleSales = {}; // bundle_code -> { bills, items, qty, amount, quotations[] }
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await fetch(`${API_BASE}/emp_quotation/search/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromDate: from,
          toDate: to,
          saleName: '',
          paymentStatus: ['approved'],
          page,
          limit: 100
        })
      });
      const result = await response.json();
      if (!result.status || !result.data?.data) break;

      const items = result.data.data;
      totalPages = result.data.pagination?.total_pages || 1;
      setProgress({ current: page, total: totalPages });

      // Fetch product details for each quotation in parallel (batches of 10)
      for (let i = 0; i < items.length; i += 10) {
        const batch = items.slice(i, i + 10);
        const details = await Promise.allSettled(
          batch.map(item =>
            fetch(`${API_BASE}/emp_quotation/${item.qn_quotation_no_code}`)
              .then(r => r.json())
              .then(d => ({ quotation: item, products: d.data?.products || [] }))
          )
        );

        details.forEach(result => {
          if (result.status !== 'fulfilled') return;
          const { quotation, products } = result.value;
          products.forEach(p => {
            const bc = p.bundle_code;
            if (!bc || bc === 'None' || bc === '') return;

            if (!bundleSales[bc]) {
              bundleSales[bc] = {
                bundle_code: bc,
                bills: new Set(),
                bill_set_qty: {},
                item_count: 0,
                total_qty: 0,
                total_amount: 0,
                quotation_details: []
              };
            }
            bundleSales[bc].bills.add(quotation.qn_quotation_no_code);
            // นับจำนวนชุดจาก qty ของ product แรกในแต่ละบิล (ถ้าซื้อ 2 ชุดใน 1 บิล qty จะเป็น 2)
            if (!(quotation.qn_quotation_no_code in bundleSales[bc].bill_set_qty)) {
              bundleSales[bc].bill_set_qty[quotation.qn_quotation_no_code] = p.product_quantity || 1;
            }
            bundleSales[bc].item_count += 1;
            bundleSales[bc].total_qty += p.product_quantity || 0;
            bundleSales[bc].total_amount += p.product_rate_pice || 0;
          });
        });
      }

      page++;
    }

    // Convert Sets to counts
    return Object.values(bundleSales).map(s => ({
      ...s,
      bill_count: s.bills.size,
      set_count: Object.values(s.bill_set_qty).reduce((sum, q) => sum + q, 0),
      bills: undefined,
      bill_set_qty: undefined
    }));
  };

  const fetchData = async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError('');
    setProgress({ current: 0, total: 0 });

    try {
      // Fetch bundles catalog
      const bundlesRes = await fetch(`${API_BASE}/setup-product/bundles`);
      const bundlesData = await bundlesRes.json();
      if (!bundlesData.success) throw new Error('ไม่สามารถดึงข้อมูลเซตได้');
      const bundleList = bundlesData.data || [];

      // Fetch bundle sales from quotations (by bundle_code)
      const salesData = await fetchBundleSalesFromQuotations(dateFrom, dateTo);

      // Build sales lookup
      const salesMap = {};
      salesData.forEach(s => { salesMap[s.bundle_code] = s; });

      // Merge
      const merged = bundleList.map(bundle => {
        const sales = salesMap[bundle.num_name_products] || null;
        return {
          ...bundle,
          sales_bills: sales ? sales.bill_count : 0,
          sales_items: sales ? sales.item_count : 0,
          sales_qty: sales ? sales.set_count : 0,
          sales_amount: sales ? sales.total_amount : 0,
          has_sales: !!sales
        };
      });

      // Also add bundle_codes that exist in sales but not in bundle catalog
      salesData.forEach(s => {
        if (!bundleList.find(b => b.num_name_products === s.bundle_code)) {
          merged.push({
            num_name_products: s.bundle_code,
            name_setup: s.bundle_code,
            item_count: 0,
            total_items: 0,
            price_a: null,
            price_b: null,
            price_c: null,
            price_d: null,
            shipping: '0',
            bundle_image: null,
            is_active: -1, // unknown
            sales_bills: s.bill_count,
            sales_items: s.item_count,
            sales_qty: s.set_count,
            sales_amount: s.total_amount,
            has_sales: true
          });
        }
      });

      setBundles(merged);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('เกิดข้อผิดพลาด: ' + err.message);
      setBundles([]);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  useEffect(() => {
    if (dateFrom && dateTo) fetchData();
  }, [dateFrom, dateTo]);

  // Fetch bundle detail + stock
  const fetchBundleDetail = async (bundleCode) => {
    setDetailLoading(true);
    setBundleDetail(null);
    setStockInfo(null);
    try {
      const [detailRes, stockRes] = await Promise.all([
        fetch(`${API_BASE}/setup-product/get_setup_product`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ num_name_products: bundleCode })
        }),
        fetch(`${API_BASE}/setup-product/bundles/${encodeURIComponent(bundleCode)}/stock-check`)
      ]);

      const detailData = await detailRes.json();
      if (detailData.success) setBundleDetail(detailData.data || []);

      const stockData = await stockRes.json();
      if (stockData.success) setStockInfo(stockData.data);
    } catch (err) {
      console.error('Error fetching bundle detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetail = (bundle) => {
    setSelectedBundle(bundle);
    fetchBundleDetail(bundle.num_name_products);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === 'None') return '-';
    return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 0 }).format(amount);
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
    setSortConfig({ key, direction });
  };

  const processedData = (() => {
    let data = [...bundles];

    if (filterStatus === 'active') data = data.filter(b => b.is_active === 1);
    else if (filterStatus === 'inactive') data = data.filter(b => b.is_active === 0);
    else if (filterStatus === 'has_sales') data = data.filter(b => b.has_sales);

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      data = data.filter(b =>
        (b.num_name_products || '').toLowerCase().includes(s) ||
        (b.name_setup || '').toLowerCase().includes(s)
      );
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (aVal === null || aVal === undefined || aVal === 'None') aVal = -1;
        if (bVal === null || bVal === undefined || bVal === 'None') bVal = -1;
        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }

    return data;
  })();

  // Summary
  const totalBills = bundles.reduce((sum, b) => sum + b.sales_bills, 0);
  const totalQty = bundles.reduce((sum, b) => sum + (b.sales_qty || 0), 0);
  const totalAmount = bundles.reduce((sum, b) => sum + b.sales_amount, 0);
  const bundlesWithSales = bundles.filter(b => b.has_sales).length;
  const activeBundles = bundles.filter(b => b.is_active === 1);

  const handleExportExcel = () => {
    if (processedData.length === 0) return alert('ไม่มีข้อมูลสำหรับ export');

    const headerInfo = [
      ['รายงานสินค้าจับเซต - ยอดขายจากใบเสนอราคา'],
      [`ช่วงวันที่: ${dateFrom} ถึง ${dateTo}`],
      [`วันที่ export: ${formatDate(new Date())}`],
      [''],
      ['ลำดับ', 'รหัสเซต', 'ชื่อเซต', 'สินค้าในเซต', 'ราคา A', 'ราคา B', 'ราคา C', 'สถานะ', 'จำนวนชุด', 'ยอดขาย (บาท)']
    ];

    const rows = processedData.map((b, i) => [
      i + 1, b.num_name_products, b.name_setup, b.item_count || '-',
      b.price_a || '-', b.price_b || '-', b.price_c || '-',
      b.is_active === 1 ? 'เปิด' : b.is_active === 0 ? 'ปิด' : '-',
      b.sales_bills, b.sales_amount
    ]);

    const finalData = [...headerInfo, ...rows];
    finalData.push(['']);
    finalData.push(['รวม', '', '', '', '', '', '', '', totalBills, totalAmount]);

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    ws['!cols'] = [
      { width: 6 }, { width: 35 }, { width: 50 }, { width: 10 },
      { width: 12 }, { width: 12 }, { width: 12 }, { width: 8 },
      { width: 10 }, { width: 15 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'สินค้าจับเซต');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `สินค้าจับเซต_${dateFrom}_${dateTo}.xlsx`);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Layers className="w-6 h-6 text-purple-600" />
                สินค้าจับเซต
              </h1>
              <p className="text-gray-600">วิเคราะห์ยอดขายเซตจากใบเสนอราคา (bundle_code)</p>
            </div>
            <button onClick={handleExportExcel}
              disabled={loading || processedData.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              <Download className="w-4 h-4" /> Export Excel
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Date Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" /> วันที่เริ่มต้น
              </label>
              <MaskedDatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholderText="DD/MM/YYYY"
                minDate={new Date('2025-01-01T00:00:00')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" /> วันที่สิ้นสุด
              </label>
              <MaskedDatePicker
                value={dateTo}
                onChange={setDateTo}
                placeholderText="DD/MM/YYYY"
                maxDate={new Date()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <button onClick={fetchData} disabled={loading || !dateFrom || !dateTo}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              {loading ? (
                <><Loader className="w-4 h-4 animate-spin" />
                  {progress.total > 0 ? `กำลังโหลด ${progress.current}/${progress.total}...` : 'กำลังโหลด...'}</>
              ) : (
                <><Search className="w-4 h-4" /> ค้นหา</>
              )}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {bundles.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:border-purple-300"
                 onClick={() => setFilterStatus('all')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">เซททั้งหมด</p>
                  <p className="text-2xl font-bold text-gray-900">{bundles.length}</p>
                  <p className="text-xs text-gray-400">เปิด {activeBundles.length} / ปิด {bundles.length - activeBundles.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:border-green-300"
                 onClick={() => setFilterStatus('has_sales')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">เซตที่ขายได้</p>
                  <p className="text-2xl font-bold text-green-600">{bundlesWithSales}</p>
                  <p className="text-xs text-gray-400">จาก {bundles.length} เซต</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">จำนวนบิลรวม</p>
                  <p className="text-2xl font-bold text-blue-600">{totalBills.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">ใบเสนอราคา</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">ยอดขายรวม</p>
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(totalAmount)}</p>
                  <p className="text-xs text-gray-400">บาท</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter + Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="ค้นหารหัสเซตหรือชื่อเซต..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: 'all', label: 'ทั้งหมด' },
                { key: 'has_sales', label: 'มียอดขาย' },
                { key: 'active', label: 'เปิดใช้งาน' },
                { key: 'inactive', label: 'ปิดใช้งาน' }
              ].map(f => (
                <button key={f.key} onClick={() => setFilterStatus(f.key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === f.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Bundle Table */}
        {processedData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-800">รายการสินค้าจับเซต</h4>
                <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">
                  {processedData.length} รายการ
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">#</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">รูป</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                        onClick={() => handleSort('num_name_products')}>
                      รหัส/ชื่อเซต <SortIcon columnKey="num_name_products" />
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                        onClick={() => handleSort('price_c')}>
                      ราคา C <SortIcon columnKey="price_c" />
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                        onClick={() => handleSort('is_active')}>
                      สถานะ <SortIcon columnKey="is_active" />
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 bg-green-50"
                        onClick={() => handleSort('sales_bills')}>
                      จำนวนชุด <SortIcon columnKey="sales_bills" />
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 bg-green-50"
                        onClick={() => handleSort('sales_amount')}>
                      ยอดขาย <SortIcon columnKey="sales_amount" />
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">ดู</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedData.map((bundle, index) => (
                    <tr key={bundle.num_name_products} className={`hover:bg-gray-50 ${bundle.has_sales ? '' : 'opacity-50'}`}>
                      <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2">
                        {bundle.bundle_image ? (
                          <img src={bundle.bundle_image} alt="" className="w-10 h-10 object-cover rounded border border-gray-200"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs font-mono font-medium text-purple-700">{bundle.num_name_products}</span>
                        <div className="text-sm text-gray-900">{bundle.name_setup}</div>
                        {bundle.item_count > 0 && (
                          <div className="text-xs text-gray-500">{bundle.item_count} รายการ / {bundle.total_items} ชิ้น</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-green-600 font-medium">
                        {formatCurrency(bundle.price_c)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {bundle.is_active === 1 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3" /> เปิด
                          </span>
                        ) : bundle.is_active === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <XCircle className="w-3 h-3" /> ปิด
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-bold bg-green-50">
                        {bundle.has_sales ? (
                          <span className="text-blue-600">{bundle.sales_bills.toLocaleString()}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-bold bg-green-50">
                        {bundle.has_sales ? (
                          <span className="text-green-600">{formatCurrency(bundle.sales_amount)}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => handleViewDetail(bundle)}
                          className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors inline-flex items-center gap-1">
                          <Eye className="w-3 h-3" /> ดู
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan="5" className="px-3 py-3 text-sm font-bold text-gray-900">รวมทั้งหมด</td>
                    <td className="px-3 py-3 text-sm text-right font-bold text-blue-600 bg-green-50">
                      {totalBills.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-sm text-right font-bold text-green-600 bg-green-50">
                      {formatCurrency(totalAmount)}
                    </td>
                    <td className="px-3 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center gap-3">
              <Loader className="w-8 h-8 animate-spin text-purple-600" />
              <span className="text-gray-500">
                {progress.total > 0
                  ? `กำลังสแกนใบเสนอราคา... (หน้า ${progress.current}/${progress.total})`
                  : 'กำลังโหลดข้อมูลเซต...'}
              </span>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Layers className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2 text-gray-500">ไม่พบข้อมูลสินค้าจับเซต</h3>
            <p className="text-gray-400 text-sm">กรุณาเลือกช่วงวันที่และกดปุ่ม "ค้นหา"</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedBundle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-purple-600" />
                  {selectedBundle.name_setup}
                </h3>
                <p className="text-sm text-gray-500 font-mono">{selectedBundle.num_name_products}</p>
              </div>
              <button onClick={() => { setSelectedBundle(null); setBundleDetail(null); setStockInfo(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Sales Summary */}
              {selectedBundle.has_sales && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> ยอดขาย ({dateFrom} ถึง {dateTo})
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{selectedBundle.sales_bills.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">จำนวนบิล</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedBundle.sales_amount)}</p>
                      <p className="text-xs text-gray-500">ยอดขายรวม (บาท)</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bundle Info */}
              <div className="flex gap-4">
                {selectedBundle.bundle_image && (
                  <img src={selectedBundle.bundle_image} alt="" className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">สถานะ</p>
                    {selectedBundle.is_active === 1 ? (
                      <span className="text-green-600 font-medium">เปิดใช้งาน</span>
                    ) : selectedBundle.is_active === 0 ? (
                      <span className="text-red-600 font-medium">ปิดใช้งาน</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">สินค้าในเซต</p>
                    <p className="font-bold text-gray-900">{selectedBundle.item_count || '-'} รายการ</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">ค่าขนส่ง</p>
                    <p className="font-bold text-gray-900">{formatCurrency(parseFloat(selectedBundle.shipping || 0))} บาท</p>
                  </div>
                  {stockInfo && (
                    <div className={`rounded-lg p-3 ${stockInfo.max_possible_sets > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className="text-xs text-gray-500">ประกอบได้สูงสุด</p>
                      <p className={`font-bold ${stockInfo.max_possible_sets > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stockInfo.max_possible_sets.toLocaleString()} ชุด
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              {selectedBundle.price_a && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">ราคาตามระดับลูกค้า</h4>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'ราคา A', value: selectedBundle.price_a, bg: 'bg-blue-50', text: 'text-blue-600' },
                      { label: 'ราคา B', value: selectedBundle.price_b, bg: 'bg-green-50', text: 'text-green-600' },
                      { label: 'ราคา C', value: selectedBundle.price_c, bg: 'bg-orange-50', text: 'text-orange-600' },
                      { label: 'ราคา D', value: selectedBundle.price_d, bg: 'bg-purple-50', text: 'text-purple-600' }
                    ].map(tier => (
                      <div key={tier.label} className={`${tier.bg} rounded-lg p-3 text-center`}>
                        <p className="text-xs text-gray-500">{tier.label}</p>
                        <p className={`text-lg font-bold ${tier.text}`}>{formatCurrency(tier.value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              {detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-purple-600 mr-2" />
                  <span className="text-gray-500">กำลังโหลดรายละเอียด...</span>
                </div>
              ) : bundleDetail && bundleDetail.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">สินค้าในเซต ({bundleDetail.length} รายการ)</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">รหัสสินค้า</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ชื่อสินค้า</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">จำนวน/เซต</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">ราคา A/ชิ้น</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">ราคา C/ชิ้น</th>
                          {stockInfo && <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">คงเหลือ</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {bundleDetail.map((item, i) => {
                          const stockItem = stockInfo?.items?.find(s => s.code === item.code_setup_all);
                          return (
                            <tr key={item.num_setup_run} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-500">{i + 1}</td>
                              <td className="px-3 py-2 text-sm font-mono text-purple-700">{item.code_setup_all}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.setup_products_details}</td>
                              <td className="px-3 py-2 text-sm text-right font-medium text-blue-600">{item.setup_amount}</td>
                              <td className="px-3 py-2 text-sm text-right text-gray-600">
                                {item.distributed_price_a ? formatCurrency(item.distributed_price_a) : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-green-600 font-medium">
                                {item.distributed_price_c ? formatCurrency(item.distributed_price_c) : '-'}
                              </td>
                              {stockInfo && (
                                <td className="px-3 py-2 text-sm text-right">
                                  {stockItem ? (
                                    <span className={stockItem.status === 'sufficient' ? 'text-green-600' : 'text-red-600'}>
                                      {stockItem.available_qty.toLocaleString()}
                                    </span>
                                  ) : '-'}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* Bottleneck */}
              {stockInfo?.bottleneck_items?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <h4 className="font-semibold text-yellow-800">สินค้าที่จำกัดการประกอบ</h4>
                  </div>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {stockInfo.bottleneck_items.map((item, i) => (
                      <li key={i}>{item.name} ({item.code}) - คงเหลือ {item.available_qty.toLocaleString()} / ต้องการ {item.required_per_set} ต่อเซต</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BundleProductsComponent;
